import { useEffect, useState } from "react";
import { Landmark, Receipt, TrendingDown, Wallet } from "lucide-react";
import { cockpit } from "@/lib/cockpitAuth";

const EUR = new Intl.NumberFormat("fr-FR", {
    style: "currency", currency: "EUR", maximumFractionDigits: 0,
});
const fmt = (n) => EUR.format(Number(n) || 0);

// ─────────────────────────────────────────────────────────────
// DossierAdminImpact — Impact administratif d'un dossier (interne)
// Liée automatiquement à l'onglet Administratif & Déclarations.
// ─────────────────────────────────────────────────────────────
export default function DossierAdminImpact({ dossierId }) {
    const [impact, setImpact] = useState(null);
    const [err, setErr] = useState("");

    useEffect(() => {
        let alive = true;
        if (!dossierId) return undefined;
        cockpit
            .get(`/admin/dossier-impact/${dossierId}`)
            .then(({ data }) => {
                if (alive) setImpact(data);
            })
            .catch((e) => {
                if (alive)
                    setErr(
                        e?.response?.data?.detail
                        || "Impact administratif indisponible",
                    );
            });
        return () => {
            alive = false;
        };
    }, [dossierId]);

    if (err) {
        return (
            <section className="border-t border-white/[0.06] py-12">
                <div className="text-[13px] text-[#e89090] border-l-2 border-[#e89090] pl-4 py-1">
                    {err}
                </div>
            </section>
        );
    }
    if (!impact) {
        return (
            <section className="border-t border-white/[0.06] py-12">
                <p className="text-[12px] text-[#f4ecd8]/40 italic font-light">
                    Calcul de l'impact administratif…
                </p>
            </section>
        );
    }

    const items = [
        { label: "Honoraires HT", value: impact.honoraires_ht, Icon: Receipt },
        { label: `TVA générée · ${impact.vat_rate}%`, value: impact.vat_generated, Icon: Receipt, muted: true },
        { label: "Montant TTC", value: impact.ttc, Icon: Receipt },
        { label: "Encaissement prévu", value: impact.encaissement_prevu, Icon: TrendingDown, muted: true },
        { label: "Encaissement réalisé", value: impact.encaissement_realise, Icon: Wallet, accent: true },
        { label: "Provision IS estimée", value: impact.provision_is, Icon: Landmark, muted: true },
        { label: "Impact trésorerie", value: impact.impact_treasury, Icon: Wallet, accent: true },
    ];

    return (
        <section
            data-testid="dossier-admin-impact"
            className="border-t border-white/[0.06] py-12"
        >
            <div className="overline text-[#d4af37]/70 mb-3 flex items-center gap-2">
                <Landmark strokeWidth={1.1} size={11} />
                Impact administratif · interne
            </div>
            <h2
                className="font-serif text-[22px] sm:text-[26px] text-[#f4ecd8] leading-tight mb-7"
                style={{ fontWeight: 400 }}
            >
                Contribution du dossier à la fiscalité XV.
            </h2>

            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-px bg-white/[0.05]">
                {items.map((it) => (
                    <div
                        key={it.label}
                        data-testid={`dossier-impact-${it.label.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "")}`}
                        className="bg-[#070d18] p-5"
                    >
                        <div className="flex items-center gap-2 mb-2.5 text-[#d4af37]/55">
                            <it.Icon strokeWidth={1.1} size={12} />
                            <span className="text-[9.5px] tracking-[0.22em] uppercase leading-tight">
                                {it.label}
                            </span>
                        </div>
                        <div
                            className={`font-serif text-[17px] sm:text-[18px] leading-none tabular-nums ${
                                it.accent
                                    ? "text-[#d4af37]"
                                    : it.muted
                                      ? "text-[#f4ecd8]/65"
                                      : "text-[#f4ecd8]"
                            }`}
                            style={{ fontWeight: 400 }}
                        >
                            {fmt(it.value)}
                        </div>
                    </div>
                ))}
            </div>

            <p className="mt-5 text-[12px] text-[#f4ecd8]/40 italic font-light leading-relaxed border-l-2 border-[#d4af37]/30 pl-4">
                Ces montants remontent automatiquement dans l'onglet
                Administratif & Déclarations.
            </p>
        </section>
    );
}
